/**
 * Screen-scoped design tokens for the seat keep-picker.
 *
 * The dialog shows the same people, in the same chrome, as the Settings → Team
 * table, so every value here is the Settings palette: identical ink, muted and
 * divider colours, the 28px monogram, the status colours and the 14px card
 * radius. Kept local like the other `*.tokens.ts` files — once the settings
 * screens are all re-skinned these get promoted into the MUI theme.
 */

import { contentCard, inkfree, notification, signer, white } from "theme/colors";

export const seatReductionTokens = {
  color: {
    /** Deep brand ink — headings and member names. */
    ink: signer.ink,
    /** Secondary text: email, helper copy, counters. */
    muted: signer.text.meta,
    /**
     * The selection caption. Uses the muted step rather than the lighter
     * `#9B96AE` the design shows for column headers: at 11.5px that one lands
     * near 3.5:1 on white, short of WCAG AA for small text.
     */
    subtle: signer.text.meta,
    surface: white,
    /** Card chrome around the list. */
    cardBorder: contentCard.borderMuted,
    /** Summary strip fill. */
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    /** Rule between rows. */
    rowDivider: signer.border.divider,
    /** Monogram avatar — neutral, matching the Team table. */
    avatarBg: signer.surface.muted,
    avatarText: signer.ink,
    /** A kept row. */
    selectedBg: signer.orange.tint,
    hoverBg: signer.surface.subtle,
    /**
     * STATUS text, at 12px — normal-size text, so it needs 4.5:1 on white.
     * The design's own status colours don't reach it (`#1E9E5A` ≈ 3.4:1,
     * `#9B96AE` ≈ 2.8:1), so these are darkened: 5.0:1 and 5.5:1 respectively.
     */
    statusActive: "#157F49",
    /** Invited but not yet accepted — already holds a seat, so it isn't grey. */
    statusInvited: signer.orange.dark,
    statusInactive: signer.text.meta,
    /** Progress meter, matching the Team seats card. */
    meterTrack: signer.border.divider,
    accent: signer.orange.main,
    /**
     * Checkbox checked-state fill — a solid selected-state fill, so per the
     * standing brand-colour rule it's the app brand red, not `accent` above.
     */
    checkedFill: inkfree.main,
    /** Selection complete. */
    success: notification.success.icon,
  },

  font: {
    title: "17px",
    intro: "12.5px",
    summaryCount: "13px",
    summaryHint: "11.5px",
    name: "13px",
    email: "11.5px",
    status: "12px",
    avatar: "10px",
    empty: "12.5px",
  },

  weight: {
    strong: 700,
    medium: 600,
    regular: 400,
  },

  radius: {
    card: "14px",
    control: "9px",
    pill: "999px",
    avatar: "50%",
  },

  border: {
    hairline: "1px",
  },

  space: {
    rowY: "12px",
    rowX: "16px",
    nameGap: "12px",
    summaryPadding: "14px",
    sectionGap: "14px",
    emptyY: "34px",
  },

  size: {
    avatar: "28px",
    meterHeight: "6px",
    /** Keeps the footer on screen no matter how many people are listed. */
    listMaxHeight: "42vh",
  },
} as const;

export default seatReductionTokens;
